<template>
    <tailwind-layout :title="property.full_address + ' Applications'">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden md:flex justify-between items-center"><!-- Block for lg and larger -->
                <div class="flex items-center space-x-3">
                    <h1 class="font-semibold text-2xl leading-tight">{{ property.full_address }}</h1>
                    <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                        <span :class="[property.status === 1 ? 'text-green-700 bg-green-50 ring-green-600/20' : 'text-red-700 bg-red-50 ring-red-600/10', 'rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset']">
                            {{ property.status === 1 ? 'Active' : 'Paused' }}
                        </span>
                    </span>
                </div>
                <div class="flex items-center space-x-4" data-demo-target="property-actions">
                    <span class="isolate inline-flex rounded-md shadow-sm">
                        <inertia-link :href="route('properties.index')"
                                      class="relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
                            <font-awesome-icon :icon="faSquareChevronLeft" class="text-gray-900" aria-hidden="true" />
                            <span class="ml-2">Back</span>
                        </inertia-link>
                        <button v-if="property.is_renters_insurance"
                                type="button"
                                class="relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 ring-1 ring-inset ring-gray-300 focus:z-10"
                                @click="showRentersInsuranceModal = true;">
                            <font-awesome-icon :icon="faShieldCheck" class=" text-gray-900" aria-hidden="true" />
                            <span class="ml-2">Renters Insurance</span>
                        </button>
                        <button v-if="!property.deleted_at"
                                type="button"
                                class="relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 ring-1 ring-inset ring-gray-300 focus:z-10"
                                @click="togglePropertyStatus()"
                                :disabled="isTogglingStatus">
                            <font-awesome-icon :icon="property.status === 1 ? faPause : faPlay" class="text-gray-900" aria-hidden="true" />
                            <span class="ml-2">{{ property.status === 1 ? 'Pause' : 'Activate' }}</span>
                        </button>
                        <button v-if="!property.deleted_at"
                                type="button"
                                class="relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 ring-1 ring-inset ring-gray-300 focus:z-10"
                                @click="showArchiveModal = true;">
                            <font-awesome-icon :icon="faBoxArchive" class=" text-gray-900" aria-hidden="true" />
                            <span class="ml-2">Archive</span>
                        </button>
                        <inertia-link :href="route('properties.edit', property.id)"
                                      class="relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 ring-1 ring-inset ring-gray-300 focus:z-10">
                            <font-awesome-icon :icon="faPenToSquare" class=" text-gray-900" aria-hidden="true" />
                            <span class="ml-2">Edit</span>
                        </inertia-link>
                        <button 
                            @click="showPropertyShareModal = true"
                            title="Copy link to clipboard"
                            class="relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 ring-1 ring-inset ring-gray-300 focus:z-10"
                            data-demo-target="copy-link-button"
                        >
                            <font-awesome-icon :icon="faArrowUpRightFromSquare" class="text-gray-900" />
                            <span class="ml-2">Share</span>
                        </button>
                    </span>
                </div>
            </div>

            <div class="px-6 lg:px-8 mx-auto flex flex-col justify-center md:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex flex-col my-4">
                    <div class="flex items-center space-x-3 mb-4">
                        <h1 class="font-semibold text-2xl leading-tight">{{ property.full_address }}</h1>
                        <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                            <span :class="[property.status === 1 ? 'text-green-700 bg-green-50 ring-green-600/20' : 'text-red-700 bg-red-50 ring-red-600/10', 'rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset']">
                                {{ property.status === 1 ? 'Active' : 'Paused' }}
                            </span>
                        </span>
                    </div>
                    <span class="isolate inline-flex my-4">
                        <inertia-link :href="route('properties.index')"
                                      class="relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
                            <font-awesome-icon :icon="faSquareChevronLeft" class="text-gray-900" aria-hidden="true" />
                            <span class="ml-2">Back</span>
                        </inertia-link>
                        <button v-if="property.is_renters_insurance"
                                type="button"
                                class="relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 ring-1 ring-inset ring-gray-300 focus:z-10"
                                @click="showRentersInsuranceModal = true;">
                            <font-awesome-icon :icon="faShieldCheck" class="text-gray-900" aria-hidden="true" />
                            <span class="ml-2">Renters Insurance</span>
                        </button>
                        <button v-if="!property.deleted_at"
                                type="button"
                                class="relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 ring-1 ring-inset ring-gray-300 focus:z-10"
                                @click="togglePropertyStatus()"
                                :disabled="isTogglingStatus">
                            <font-awesome-icon :icon="property.status === 1 ? faPause : faPlay" class="text-gray-900" aria-hidden="true" />
                            <span class="ml-2">{{ property.status === 1 ? 'Pause' : 'Activate' }}</span>
                        </button>
                        <button v-if="!property.deleted_at"
                                type="button"
                                class="relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 ring-1 ring-inset ring-gray-300 focus:z-10"
                                @click="showArchiveModal = true;">
                            <font-awesome-icon :icon="faBoxArchive" class="text-gray-900" aria-hidden="true" />
                            <span class="ml-2">Archive</span>
                        </button>
                        <!--                        relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-white bg-red-700 hover:bg-black hover:text-white ring-1 ring-inset ring-gray-300 focus:z-10-->
                        <inertia-link :href="route('properties.edit', property.id)"
                                      class="relative -ml-px inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 ring-1 ring-inset ring-gray-300 focus:z-10">
                            <font-awesome-icon :icon="faPenToSquare" class=" text-gray-900" aria-hidden="true" />
                            <span class="ml-2">Edit</span>
                        </inertia-link>
                        <button 
                            @click="showPropertyShareModal = true"
                            title="Copy link to clipboard"
                            class="relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 bg-white hover:bg-gray-50 ring-1 ring-inset ring-gray-300 focus:z-10"
                            data-demo-target="copy-link-button"
                        >
                            <font-awesome-icon :icon="faArrowUpRightFromSquare" class="text-gray-900" />
                            <span class="ml-2">Share</span>
                        </button>
                    </span>
                </div>
            </div>
        </template>
        <trashed-message v-if="property.deleted_at" class="mb-6" @restore="showRestoreModal = true;">
            This property has been archived.
        </trashed-message>

        <div class="overflow-hidden">
            <div class="space-y-6 lg:col-start-1 lg:col-span-6">
                <section v-show="!property.deleted_at" aria-labelledby="applications">
                    <div v-if="!isUserExperianVerified" class="rounded-md bg-yellow-50 p-4 dark:bg-yellow-500/10 dark:outline dark:outline-1 dark:outline-yellow-500/15 mt-2">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <font-awesome-icon :icon="faInfoCircle" class="text-yellow-400 dark:text-yellow-300" />
                            </div>
                            <div class="ml-3 flex-1 md:flex md:justify-between">
                                <p class="text-sm text-yellow-700 dark:text-yellow-100/80">Verify your identity in order to view an applicant's Credit Report.</p>
                                <inertia-link :href="route('experian-verification.create')" class="whitespace-nowrap font-bold text-sm text-yellow-700 hover:text-yellow-600 dark:text-yellow-300 dark:hover:text-yellow-200">
                                    Verify Now
                                    <span aria-hidden="true"> &rarr;</span>
                                </inertia-link>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="property.applications.length" class="rounded-md bg-green-50 p-4 dark:bg-green-500/10 dark:outline dark:outline-1 dark:outline-green-500/15 mt-2">
                        <div class="flex">
                            <div class="flex-shrink-0">
                                <font-awesome-icon :icon="faInfoCircle" class="text-green-400 dark:text-green-300" />
                            </div>
                            <div class="ml-3 flex-1 md:flex md:justify-between">
                                <p class="text-sm text-green-700 dark:text-green-100/80">Verifying applicants' income and employment with enhanced screening reduces defaults on rent payments by 30%.</p>
                            </div>
                        </div>
                    </div>
                    <div class="flex justify-between py-5">
                        <h2 id="applications" class="text-xl leading-6 font-bold text-gray-900">
                            Applications
                        </h2>
                        <div class="flex flex-col items-end">
                            <div v-show="property.applications.length > 0" @click="show_archived_applications()">
                                <SwitchGroup as="div" class="flex items-center">
                                    <Switch :class="[showArchived ? 'bg-rent-risk-blue-400' : 'bg-gray-200', 'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2']">
                                        <span aria-hidden="true" :class="[showArchived ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                                    </Switch>
                                    <SwitchLabel as="span" class="ml-3 text-sm">
                                        <span class="font-medium text-gray-900">Show Archived Applications</span>
                                    </SwitchLabel>
                                </SwitchGroup>
                            </div>
                        </div>
                    </div>
                    <div v-show="property.applications.length === 0">
                        <div class="rounded bg-blue-50 border border-t-blue-800 border-t-4 border-blue-400 py-4 px-2">
                            <div class="flex">
                                <div class="flex-shrink-0">
                                    <font-awesome-icon :icon="faInfoCircle" class=" text-blue-700" aria-hidden="true" />
                                </div>
                                <div class="ml-3 flex-1 md:flex md:justify-between">
                                    <p class="text-sm text-blue-700">
                                        No Applications have been submitted for this property.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-show="property.applications.length > 0" class="rounded-md lg:shadow shadow-none overflow-x-auto">
                        <div class="flex flex-col">
                            <div class="overflow-x-auto">
                                <div class="align-middle inline-block min-w-full">
                                    <div class="shadow overflow-hidden lg:border" data-demo-target="applications-list">
                                        <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                            <thead class="bg-blue-50">
                                            <tr>
                                                <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Applicant Name</th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Started Date</th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center" data-demo-target="widget-paid">Paid</th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center" data-demo-target="widget-report-shared">Report Shared</th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center" data-demo-target="widget-financial-docs">Financial Documents</th>
                                                <th v-if="property.required_two_step" scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center">Criminal/Background Report</th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-center"></th>
                                            </tr>
                                            </thead>
                                            <tbody class="">
                                                <tr
                                                    v-for="(application, index) in property.applications"
                                                    :key="index"
                                                    :class="[
                                                        (application.agent_application_display === 0 ? 'bg-red-50 hover:bg-red-100 hover:cursor-pointer border-b' : 'bg-white hover:bg-yellow-50 hover:cursor-pointer border-b')
                                                    ]"
                                                >
                                                <template v-if="showArchived && application.agent_application_display === 0 || application.agent_application_display === 1">
                                                    <td
                                                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6 flex flex-row justify-between">
                                                        <div class="float-left">
                                                            <p  @click="clickList(application)">{{ application.user.name }}</p>
                                                            <p  @click="clickList(application)" class="text-gray-500">{{ application.user.email }}</p>
                                                            <small v-show="showErrorMessage[application.id]" class="text-red-700">Please ask the applicant to submit personal information or you can archive the application to hide from this table.</small>
                                                            <button v-if="application.required_addons.length" @click="showApplicationEnhancedScreeningOptions(application)" class="w-min"><small class="text-blue-600 underline font-bold">Enhanced Screening Results</small></button>
                                                        </div>
                                                        <div v-if="application.additional_applicants_share_hash || application.application_parent_id > 0">
                                                            <font-awesome-icon icon="fa-people-roof" :size="'2x'" class="pt-1 pr-2" />
                                                        </div>
                                                        <!-- ADD COLOR TO DENOTE IF APPLICATION IS ARCHIVED AND ADD BUTTON TO UNARCHIVE-->
                                                        <!-- 2 step process-->
                                                        <!-- change affiliiate link from char 36 to varchar 255-->
                                                    </td>
                                                    <td @click="clickList(application)"
                                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center"><time :datetime="application.created_at">{{ application.created_at }}</time></td>
                                                    <td @click="clickList(application)"
                                                        class="whitespace-nowrap text-sm text-gray-500 text-center">
                                                        <span class="text-green-600 ring-green-600/20 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium" v-if="application.paid">
                                                            <font-awesome-icon :icon="faCircleCheck" :size="'2x'" />
                                                        </span>
                                                        <span class="text-red-700 ring-red-600/10 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium" v-else>
                                                            <font-awesome-icon :icon="faCircleXmark" :size="'2x'" />
                                                        </span>
                                                    </td>
                                                    <td @click="clickList(application)"
                                                        class="whitespace-nowrap text-sm text-gray-500 text-center">
                                                        <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                                                            <span class="text-green-600 ring-green-600/20 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium" v-if="application.share_id">
                                                                <font-awesome-icon :icon="faCircleCheck" :size="'2x'" />
                                                            </span>
                                                            <span class="text-red-700 ring-red-600/10 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium" v-else>
                                                                <font-awesome-icon :icon="faCircleXmark" :size="'2x'" />
                                                            </span>
                                                        </span>
                                                    </td>
                                                    <td @click="clickList(application)"
                                                        class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                                        <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                                                            <span class="text-green-600 ring-green-600/20 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium" v-if="application.documents.length > 0">
                                                                <font-awesome-icon :icon="faCircleCheck" :size="'2x'" />
                                                            </span>
                                                            <span class="text-red-700 ring-red-600/10 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium" v-else>
                                                                <font-awesome-icon :icon="faCircleXmark" :size="'2x'" />
                                                            </span>
                                                        </span>
                                                    </td>
                                                    <template v-if="property.required_two_step">
                                                        <template v-if="application.agent_requests_criminal_background && !application.tenant_approves_criminal_background">
                                                            <td @click="clickList(application)" class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                                                Request Sent
                                                            </td>
                                                        </template>
                                                        <template v-else-if="application.agent_requests_criminal_background && application.tenant_approves_criminal_background">
                                                            <td @click="clickList(application)"
                                                                class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                                                <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                                                                    <span class="text-green-600 ring-green-600/20 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium" v-if="application.share_id">
                                                                        <font-awesome-icon :icon="faCircleCheck" :size="'2x'" />
                                                                    </span>
                                                                    <span class="text-red-700 ring-red-600/10 inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium" v-else>
                                                                        <font-awesome-icon :icon="faCircleXmark" :size="'2x'" />
                                                                    </span>
                                                                </span>
                                                            </td>
                                                        </template>
                                                        <template v-else>
                                                            <td v-if="application.report"
                                                                class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                                                <button
                                                                    @click.prevent="openRequestModalAlert(application)"
                                                                    class="inline-flex items-center justify-center px-2 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                                                                    Click to Request
                                                                </button>
                                                            </td>
                                                            <td @click="clickList(application)"
                                                                    class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center" v-else>
                                                                N/A
                                                            </td>
                                                        </template>
                                                    </template>
                                                    <td class="text-center">
                                                        <button data-demo-target="enhanced-screening-button" v-if="application.required_addons.length < addons.length" @click="openEnhancedScreeningOptionsModal(application)" class="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mr-2">Verify this Applicant</button>
                                                        <template v-if="application.personal_information && application.agent_application_display === 1">
                                                            <button @click="clickList(application)" class="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">View Application</button>
                                                        </template>
                                                        <template v-else>
                                                            <button v-if="application.agent_application_display === 0" @click="restoreApplication(application)" class="rounded bg-green-50 px-2 py-1 text-xs font-semibold text-green-700 shadow-sm hover:bg-green-100">Restore Application</button>
                                                            <button v-else @click="archiveApplication(application)"  class="rounded bg-red-50 px-2 py-1 text-xs font-semibold text-red-500 shadow-sm ring-1 ring-inset ring-red-300 hover:bg-red-100">Archive Application</button>
                                                        </template>
                                                    </td>
                                                </template>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                            <li v-for="(application, index) in property.applications" :key="index">
                                            <template v-if="showArchived && application.agent_application_display === 0 || application.agent_application_display === 1">
                                                <div :class="[application.agent_application_display === 0 ? 'bg-red-50 hover:bg-red-100 hover:outline-red-500 divide-red-300' : 'bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200', 'col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border']">
<!--                                                    <div class="flex text-sm justify-end p-4" v-if="application.additional_applicants_share_hash || application.application_parent_id > 0">-->
<!--                                                        <font-awesome-icon icon="fa-people-roof" />-->
<!--                                                    </div>-->

                                                    <div class="flex text-sm justify-between p-4">
                                                        <span class="text-gray-900 font-bold pr-2">
                                                            Name
                                                            <span class="text-sm pl-4" v-if="application.additional_applicants_share_hash || application.application_parent_id > 0">
                                                                <font-awesome-icon icon="fa-people-roof" />
                                                            </span>
                                                        </span>
                                                        <span class="text-gray-500">{{ application.user.name }}</span>
                                                    </div>

                                                    <div class="flex text-sm justify-between p-4">
                                                        <span class="text-gray-900 font-bold pr-2">Email</span>
                                                        <span class="text-gray-500 truncate">{{ application.user.email }}</span>
                                                    </div>

                                                    <div class="flex text-sm justify-between p-4">
                                                        <span class="text-gray-900 font-bold pr-2">Date Started</span>
                                                        <span class="text-gray-500">{{ application.created_at }}</span>
                                                    </div>

                                                    <div class="flex text-sm justify-between p-4">
                                                        <span class="text-gray-900 font-bold pr-2">Paid</span>
                                                        <span class="text-green-600 ring-green-600/20 inline-flex items-center gap-x-1.5 rounded-md text-xs font-medium" v-if="application.paid">
                                                            <font-awesome-icon :icon="faCircleCheck" :size="'2x'" />
                                                        </span>
                                                        <span class="text-red-700 ring-red-600/10 inline-flex items-center gap-x-1.5 rounded-md text-xs font-medium" v-else>
                                                            <font-awesome-icon :icon="faCircleXmark" :size="'2x'" />
                                                        </span>
                                                    </div>

                                                    <div class="flex text-sm justify-between p-4">
                                                        <span class="text-gray-900 font-bold pr-2">Report Shared</span>
                                                        <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                                                            <span class="text-green-600 ring-green-600/20 inline-flex items-center gap-x-1.5 rounded-md text-xs font-medium" v-if="application.share_id">
                                                                <font-awesome-icon :icon="faCircleCheck" :size="'2x'" />
                                                            </span>
                                                            <span class="text-red-700 ring-red-600/10 inline-flex items-center gap-x-1.5 rounded-md text-xs font-medium" v-else>
                                                                <font-awesome-icon :icon="faCircleXmark" :size="'2x'" />
                                                            </span>
                                                        </span>
                                                    </div>

                                                    <div class="flex text-sm justify-between p-4">
                                                        <span class="text-gray-900 font-bold">Financial Documents</span>
                                                        <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                                                            <span class="text-green-600 ring-green-600/20 inline-flex items-center gap-x-1.5 rounded-md text-xs font-medium" v-if="application.documents.length > 0">
                                                                <font-awesome-icon :icon="faCircleCheck" :size="'2x'" />
                                                            </span>
                                                            <span class="text-red-700 ring-red-600/10 inline-flex items-center gap-x-1.5 rounded-md text-xs font-medium" v-else>
                                                                <font-awesome-icon :icon="faCircleXmark" :size="'2x'" />
                                                            </span>
                                                        </span>
                                                    </div>

                                                    <div v-if="!application.personal_information" class="flex text-sm justify-between items-center p-4">
                                                        <span class="text-gray-900 font-bold">Personal Information</span>
                                                        <span class="text-red-700 ring-red-600/10 inline-flex items-center gap-x-1.5 rounded-md text-xs font-medium">
                                                            <font-awesome-icon :icon="faCircleXmark" :size="'2x'" />
                                                        </span>
                                                    </div>
                                                    <div v-if="!application.personal_information" class="flex text-sm text-red-700 font-bold p-4">
                                                        Please ask the applicant to submit personal information or you can archive the application to hide from this page.
                                                    </div>

                                                    <template v-if="property.required_two_step">
                                                        <div class="flex text-sm justify-between items-center p-4">
                                                            <span class="text-gray-900 font-bold">Criminal/Background Report</span>
                                                            <template v-if="application.agent_requests_criminal_background && !application.tenant_approves_criminal_background">
                                                                <span @click="clickList(application)" class="whitespace-nowrap text-sm text-green-700 font-bold text-center">
                                                                    Request Sent
                                                                </span>
                                                            </template>
                                                            <template v-else-if="application.agent_requests_criminal_background && application.tenant_approves_criminal_background">
                                                                <span @click="clickList(application)"
                                                                    class="whitespace-nowrap text-sm text-gray-500 text-center">
                                                                    <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                                                                        <span class="text-green-600 ring-green-600/20 inline-flex items-center gap-x-1.5 rounded-md text-xs font-medium" v-if="application.share_id">
                                                                            <font-awesome-icon :icon="faCircleCheck" :size="'2x'" />
                                                                        </span>
                                                                        <span class="text-red-700 ring-red-600/10 inline-flex items-center gap-x-1.5 rounded-md text-xs font-medium" v-else>
                                                                            <font-awesome-icon :icon="faCircleXmark" :size="'2x'" />
                                                                        </span>
                                                                    </span>
                                                                </span>
                                                            </template>
                                                            <template v-else>
                                                                <span v-if="application.report"
                                                                    class="whitespace-nowrap text-sm text-gray-500 text-center">
                                                                    <button
                                                                        @click.prevent="openRequestModalAlert(application)"
                                                                        class="inline-flex items-center justify-center px-2 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 uppercase">
                                                                        Request
                                                                    </button>
                                                                </span>
                                                                <span @click="clickList(application)"
                                                                    class="whitespace-nowrap text-sm text-gray-500 text-center" v-else>
                                                                    N/A
                                                                </span>
                                                            </template>
                                                        </div>
                                                    </template>
                                                    <div>
                                                        <div :class="[application.agent_application_display === 0 ? 'divide-red-300' : 'divide-gray-200','-mt-px flex divide-x']">
                                                            <div v-if="application.personal_information" class="flex w-0 flex-1">
                                                                <button
                                                                        @click="clickList(application)"
                                                                        :class="[application.agent_application_display === 0 ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900']"
                                                                >
                                                                    <font-awesome-icon icon="fa-eyes" />
                                                                    View
                                                                </button>
                                                            </div>
                                                            <div class="-ml-px flex w-0 flex-1">
                                                                <button v-if="application.agent_application_display === 0"
                                                                        @click="restoreApplication(application)"
                                                                        :class="[application.agent_application_display === 0 ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900']"
                                                                >
                                                                    <font-awesome-icon icon="fa-refresh" /> Restore
                                                                </button>
                                                                <button v-else @click="archiveApplication(application)"
                                                                        :class="[application.agent_application_display === 0 ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900']"
                                                                >
                                                                    <font-awesome-icon icon="fa-archive" /> Archive
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </template>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
        <div v-if="showRequestModal">
            <criminal-background-request-alert :application="application" :open="showRequestModal" @close-alert="showRequestModal = false" />
        </div>
        
        <div v-if="showArchiveModal">
            <TransitionRoot as="template" :show="showArchiveModal">
                <Dialog as="div" class="relative z-50" @close="showArchiveModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                            <font-awesome-icon :icon="faBoxArchive" class="h-6 w-6 text-red-600" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-xl font-semibold leading-6 text-gray-900">Are you sure you want to archive this property?</DialogTitle>
                                            <div class="mt-3">
                                                Tenants will <span class="font-bold underline">not</span> be able to apply for this property.<br>You will not be able to open or view any applications associated with this property once it is archived. You can restore it later in order to view the applications.
                                            </div>
                                            <div class="mt-3">
                                                You can also click on the Edit button for this property and choose NO under Accept Applications if you no longer want to accept applications, but still want to see this property listed on your properties page.
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showArchiveModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="destroy()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>
        <div v-if="showRestoreModal">
            <TransitionRoot as="template" :show="showRestoreModal">
                <Dialog as="div" class="relative z-50" @close="showRestoreModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                            <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to restore this property?</DialogTitle>
                                            <div class="mt-2">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showRestoreModal = false">Cancel</button>
                                        <button type="button" class="primaryButton w-1/2 ml-1 justify-center rounded-md px-3 py-2 text-sm font-semibold text-white hover:bg-black focus:outline-2 focus:outline-offset-2 focus:outline-transparent" @click="restore()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>
        <div v-if="showArchiveApplicationModal">
            <TransitionRoot as="template" :show="showArchiveApplicationModal">
                <Dialog as="div" class="relative z-50" @close="showArchiveApplicationModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                            <font-awesome-icon :icon="faBoxArchive" class="h-6 w-6 text-red-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to archive this application?</DialogTitle>
                                            <div class="mt-2">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showArchiveApplicationModal = false">Cancel</button>
                                        <inertia-link :href="route('properties.application.archive',[property.id, application.id])" class="text-center flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Confirm</inertia-link>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>
        <div v-if="showRestoreApplicationModal">
            <TransitionRoot as="template" :show="showRestoreApplicationModal">
                <Dialog as="div" class="relative z-50" @close="showRestoreApplicationModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                            <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to restore this application?</DialogTitle>
                                            <div class="mt-2">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600" @click="showRestoreApplicationModal = false">Cancel</button>
                                        <inertia-link :href="route('properties.application.restore',[property.id, application.id])" class="text-center flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-rent-risk-blue-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rent-risk-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Confirm</inertia-link>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

        <!-- Share Property Modal -->
        <share-modal 
            :show="showPropertyShareModal"
            :share-url="property.application_link"
            :share-text="shareText"
            title="Share Property Application Link"
            @close="showPropertyShareModal = false"
        />

      <ssn-acknowledgment-modal :show="!property.ssn_acknowledgment" @closeAlert="handleCloseModal"></ssn-acknowledgment-modal>
        <ca-iccra-compliance :open="showCAComplianceModal" :property="property" @close-alert="handleComplianceClose"></ca-iccra-compliance>
        <!-- Enhanced Screening Results Modal -->
        <enhanced-screening-results-modal
            :show="showEnhancedScreeningResultsModal"
            :application="application"
            :property="property"
            @close="showEnhancedScreeningResultsModal = false"
        />

        <!-- Renter's Insurance Modal -->
        <modal-jetstream :show="showRentersInsuranceModal" :max-width="'3xl'">
            <div class="rounded-md overflow-x-auto m-2">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="overflow-hidden bg-white ring-1 ring-gray-200 p-6 flex flex-col md:flex-row justify-between gap-6">
                                <!-- Left Section -->
                                <div class="md:w-1/2 space-y-4">
                                    <h2 class="text-xl font-semibold text-gray-800">Why Require Renters Insurance?</h2>
                                    <p class="text-gray-600">
                                    Requiring tenants to purchase renters insurance ensures that your property is protected in the event of unforeseen circumstances. It provides peace of mind and reduces potential liabilities for landlords.
                                    </p>
                                    <ul class="list-disc pl-5 text-gray-600 space-y-1">
                                    <li>Protection against loss of use/relocation expenses</li>
                                    <li>Personal liability coverage</li>
                                    <li>Mitigation of property damage risks</li>
                                    <li>Keeping insurance premiums low</li>
                                    <li>Managing risks associated with pets</li>
                                    </ul>
                                    <p class="text-gray-600">
                                    We encourage you to consider adding a renters insurance requirement to your leasing agreements for enhanced property protection.
                                    </p>
                                </div>

                                <!-- Right Section -->
                                <div class="md:w-1/2 bg-gray-50 border border-gray-200 rounded-xl p-6 flex flex-col items-center text-center space-y-4">
                                    <p class="text-sm font-semibold text-gray-500 tracking-wide">POWERED BY</p>
                                    <img :src="appUrl('/sure-insurance-logo.svg')" alt="Sure Insurance Logo" class="rounded-lg bg-white ring-1 ring-gray-900/10 p-2 w-20">
                                    
                                    <p class="text-gray-600">
                                    Need to request renters insurance? We've partnered with Sure to offer renters insurance. Send this link to your tenants to help them get started:
                                    </p>
                                    
                                    <div class="w-full">
                                    <input type="text" id="insurance-link" :value="property.renters_insurance_link" readonly class="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-gray-100 focus:outline-none">
                                    </div>
                                    
                                    <button
                                        @click="copyRentersInsuranceLink"
                                        class="primaryButton"
                                    >
                                        {{ isLinkCopied ? 'Link Copied!' : 'Copy Link' }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="property.renter_insurance_files.length">
                    <!-- Previous Uploaded Documents Header -->
                    <div class="max-w-3xl mx-auto mt-4 mb-4 md:flex md:items-center md:justify-between md:space-x-5 lg:max-w-7xl">
                        <h2 class="text-xl font-bold">Previously Uploaded Documents</h2>
                    </div>

                    <!-- Uploaded Documents Table -->
                    <div class="flex flex-col">
                        <div class="overflow-x-auto">
                            <div class="align-middle inline-block min-w-full">
                                <div class="shadow overflow-hidden lg:border">
                                    <table class="min-w-full divide-y divide-gray-200">
                                        <thead class="bg-blue-50">
                                            <tr>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                    Applicant
                                                </th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                    File
                                                </th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                                    Date Uploaded
                                                </th>
                                                <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="(file, index) in [...property.renter_insurance_files].reverse()"
                                                :key="index"
                                                class="bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer"
                                            >
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                    {{ file.renter_insurance.application.user.name }}
                                                    <small>({{ file.renter_insurance.application.user.email }})</small>
                                                </td>
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                    {{ file.file_name }}
                                                </td>
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900">
                                                    {{ file.formatted_created_at }}
                                                </td>
                                                <td class="whitespace-nowrap py-4 px-4 text-sm font-medium text-gray-900 flex space-x-4">
                                                    
                                                    <a target="_blank" :href="route('renters-insurance.view-file', file.id)"
                                                        class="font-medium text-indigo-600 hover:text-indigo-500">View</a>
                                                    <a target="_blank" :href="route('renters-insurance.download-file', file.id)"
                                                        class="font-medium text-indigo-600 hover:text-indigo-500">Download</a>
                                                    
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            <button @click="showRentersInsuranceModal = false" class="bg-white py-2 px-4 m-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 float-right">Close</button>
        </modal-jetstream>

        <!-- Enhanced Screening Options Modal -->
        <enhanced-screening-options-modal
            ref="enhancedScreeningModal"
            :show="showEnhancedScreeningOptionsModal"
            :application="application"
            :addons="addons"
            :has-protect-access="hasProtectAccess"
            :subscription-type="subscriptionType"
            :free-bundles-used="freeBundlesUsed"
            :payment-method-success="payment_method_success"
            @close="closeEnhancedScreeningOptionsModal"
            @submit="handleEnhancedScreeningSubmit"
        />
        <!-- Subscription Protection Component -->
        <subscription-protection
            ref="subscriptionProtection"
            :subscription-type="'protect'"
            :terms-accepted="protect_tos_accepted_at"
            :has-payment-method="is_agent_payment_method_added"
            :user-payment-method="user_payment_method"
            :stripe-data="stripe_data"
            :show-confirmation-modal="showConfirmationForSubscription"
            @success="handleSubscriptionSuccess"
            @error="handleSubscriptionError"
            @cancel="handleSubscriptionCancel"
        ></subscription-protection>

        <!-- Add Credit Card Modal (for enhanced screening, not subscription) -->
        <add-card-popup :show="showAddCardModal" :stripe_data="stripe_data" @card-added="handleCardAdded" @close="closeCardModal()"></add-card-popup>

        <!-- RAC-10008: free-plan view-application limit upsell to RiskGuard Pro -->
        <RiskGuardProReportLimitModal
            :show="showUpgradeLimitModal"
            :processing="isInSubscriptionFlow"
            @upgrade="handleUpgradeClick"
            @decline="closeUpgradeLimitModal"
        />
    
    <!-- Demo Widget -->
    <DemoWidget
        :page-identifier="'properties.show'"
        :steps="demoSteps"
        :auto-start="true"
        :show-help-icon="true"
    />
    </tailwind-layout>
</template>



<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import SelectInput from '@/Shared/SelectInput.vue'
import TrashedMessage from '@/Shared/TrashedMessage.vue'
import ProgressRing from '@/Shared/ProgressRing.vue'
import FormButton from '@/Shared/FormButton.vue'
import CriminalBackgroundRequestAlert from "../../Shared/CriminalBackgroundRequestAlert.vue";
import {Dialog, DialogPanel, DialogTitle, Switch, SwitchGroup, SwitchLabel, TransitionChild, TransitionRoot} from '@headlessui/vue'
import Modal from "@/Shared/Modal.vue";
import SsnAcknowledgmentModal from "@/Shared/SsnAcknowledgmentModal.vue";
import axios from "axios";
import CaIccraCompliance from "@/Shared/CaIccraCompliance.vue";
import AlertCard from "@/Shared/AlertCard.vue";
import ModalJetstream from "@/Jetstream/Modal.vue";
import AddCardPopup from '@/Shared/AddCardPopup.vue';
import ShareModal from './ShareModal.vue';
import DemoWidget from '@/Components/DemoWidget.vue'
import UpgradeLimitModal from '@/Shared/UpgradeLimitModal.vue';
import RiskGuardProReportLimitModal from '@/Components/RiskGuardProReportLimitModal.vue';
import SubscriptionProtection from '@/Shared/SubscriptionProtection.vue';
import EnhancedScreeningOptionsModal from '@/Components/EnhancedScreeningOptionsModal.vue';
import EnhancedScreeningResultsModal from '@/Components/EnhancedScreeningResultsModal.vue';
import { getDemoSteps } from '@/Config/demoSteps.js'
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import {
    faArrowUpRightFromSquare,
    faBoxArchive,
    faCircleCheck,
    faCircleX,
    faCircleXmark,
    faInfoCircle,
    faPause,
    faPenToSquare,
    faPlay,
    faShieldCheck,
    faSquareChevronLeft
} from '@fortawesome/pro-solid-svg-icons';
import { faCheckCircle, faXmark } from "@fortawesome/pro-light-svg-icons";


export default {
    props: {
        errors: null,
        link: null,
        states: Object,
        property: Object,
        is_agent_payment_method_added: Boolean,
        addons: Object,
        user_payment_method: Object,
        stripe_data: Array,
        hasProtectAccess: Boolean,
        subscriptionType: String,
        freeBundlesUsed: Number,
        isUserExperianVerified: Boolean,
        is_new_agent: Boolean,
        protect_tos_accepted_at: Boolean,
    },
    components: {
        AlertCard,
        SwitchLabel,
        SwitchGroup,
        Switch,
        SsnAcknowledgmentModal,
        CaIccraCompliance,
        CriminalBackgroundRequestAlert,
        TailwindLayout,
        LoadingButton,
        SelectInput,
        TrashedMessage,
        ProgressRing,
        TransitionChild,
        TransitionRoot,
        Dialog,
        DialogTitle,
        DialogPanel,
        ModalJetstream,
        FormButton,
        AddCardPopup,
        ShareModal,
        DemoWidget,
        UpgradeLimitModal,
        RiskGuardProReportLimitModal,
        SubscriptionProtection,
        EnhancedScreeningOptionsModal,
        EnhancedScreeningResultsModal
    },
    data() {
        return {
            shareText: this.property.address_1+' is available for rent in ' + this.property.city + ', ' + this.property.state + '. Apply here today!', // Customize the share message
            showErrorMessage: {},
            showArchived: false,
            application : null,
            showRequestModal: false,
            showArchiveModal : false,
            showRestoreModal : false,
            showArchiveApplicationModal: false,
            showRestoreApplicationModal: false,
            showPropertyShareModal: false,
            showCopyNotification: false,
            copyNotificationType: 'success',
            copyNotificationMessage: '',
            showCAComplianceModal: false,
            showApplicationEnhancedScreeningOptionsModal: false,
            showEnhancedScreeningOptionsModal: false,
            showEnhancedScreeningResultsModal: false,
            showRentersInsuranceModal: false,
            isLinkCopied: false,
            faArrowUpRightFromSquare,
            faCircleCheck,
            faCircleXmark,
            faCheckCircle,
            faXmark,
            faCircleX,
            faPause,
            faPlay,
            faInfoCircle,
            faShieldCheck,
            faSquareChevronLeft,
            faBoxArchive,
            faPenToSquare,
            
            form: this.$inertia.form({
                name: null,
                address_1: null,
                address_2: null,
                city: null,
                state: null,
                zip_code: null,
                link: this.link,
            }),

            enhancedScreeningForm: this.$inertia.form({
                addons: [],
            }),

            payment_method: this.user_payment_method,
            payment_method_success: null,
            showAddCardModal: false,
            pendingEnhancedScreeningData: null,
            demoSteps: getDemoSteps('properties.show'),
            showUpgradeLimitModal: false,
            pendingApplication: null,
            isInSubscriptionFlow: false,
            isSubscribingToProtect: false,
            showConfirmationForSubscription: false,
            isTogglingStatus: false,
        }
    },
    watch: {
        'user_payment_method': {
            handler(newVal) {
                // Update local payment_method when prop changes (e.g., after card is added via subscription)
                if (newVal) {
                    this.payment_method = newVal;
                }
            },
            immediate: true
        }
    },
    computed: {
        years() {
            const startYear = 1800;
            const currentYear = new Date().getFullYear();
            return Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i);
        }
    },
    methods: {
        show_archived_applications() {
            this.showArchived = !this.showArchived;
        },
        closeRequestModalAlert() {
            this.showRequestModal = false
        },
        async copyToClipboard(propertyShareLink) {
            try {
                const fullUrl = `${propertyShareLink}`;
                await navigator.clipboard.writeText(fullUrl);
                
                // Show success message
                this.showCopyNotification = true;
                this.copyNotificationType = 'success';
                this.copyNotificationMessage = 'Property Share Link copied to clipboard!';
                
                // Auto-hide after 3 seconds
                setTimeout(() => {
                    this.showCopyNotification = false;
                }, 3000);
            } catch($e) {
                // Show error message
                this.showCopyNotification = true;
                this.copyNotificationType = 'error';
                this.copyNotificationMessage = 'Failed to copy link to clipboard';
                
                // Auto-hide after 3 seconds
                setTimeout(() => {
                    this.showCopyNotification = false;
                }, 3000);
            }
        },
        openRequestModalAlert(application) {
            this.showRequestModal = true;
            this.application = application;
        },
        store() {
            this.form.post(this.route('properties.store'))
        },
        destroy() {
            this.$inertia.delete(this.route('properties.destroy', this.property.id), {
                onFinish: () => {
                    this.showArchiveModal = false;
                }
            })
        },
        restore() {
            this.$inertia.put(this.route('properties.restore', this.property.id), {}, {
                onFinish: () => {
                    this.showRestoreModal = false;
                }
            })
        },
        handleEnhancedScreeningSubmit(data) {
            // Store the selected addons for later use when payment card is added
            this.pendingEnhancedScreeningData = data;

            if(this.payment_method === null) {
                this.showAddCardModal = true;
                return false;
            }

            this.processEnhancedScreeningPayment(data);
        },

        processEnhancedScreeningPayment(data) {
            this.enhancedScreeningForm.addons = data.addons;
            this.enhancedScreeningForm.put(this.route('properties.application.required_addons', [this.property.id, this.application.id]), {
                onFinish: () => {
                    this.showEnhancedScreeningOptionsModal = false;
                    this.enhancedScreeningForm.addons = []; // setting back to empty
                    this.pendingEnhancedScreeningData = null; // clear pending data
                    // Reset modal submission state
                    if (this.$refs.enhancedScreeningModal) {
                        this.$refs.enhancedScreeningModal.resetSubmissionState();
                    }
                }
            })
        },
        async clickList(application) {
            if (!application.personal_information) {
                this.showApplicationError(application.id, 2500);
                return;
            }

            if (this.is_new_agent && !this.hasProtectAccess) {
                try {
                    const response = await axios.get(
                        route('properties.application.check-access', [application.property_id, application.id])
                    );
                    
                    if (!response.data.can_view) {
                        this.pendingApplication = application;
                        // Only show upgrade modal if not in subscription flow
                        if (!this.isInSubscriptionFlow) {
                            this.showUpgradeLimitModal = true;
                        }
                        return;
                    }
                } catch (error) {
                    if (error.response?.status === 403 || error.response?.status === 404) {
                        this.showApplicationError(application.id);
                        return;
                    }
                    if (error.response?.status >= 500) {
                        alert('Unable to verify access. Please try again later.');
                        return;
                    }
                    alert('Unable to verify access. Please check your connection and try again.');
                    return;
                }
            }

            this.navigateToApplication(application);
        },
        archiveApplication(application) {
            this.application = application;
            this.showArchiveApplicationModal = true;
        },
        restoreApplication(application) {
            this.application = application;
            this.showRestoreApplicationModal = true;
        },
        handleCloseModal() {
          axios.post(route('ssn-acknowledgment'), {
            type: 'property',
            value: this.property.id,
          });

          if(!this.property.ca_icraa_compliance && this.property.state === 'CA') {
            this.showCAComplianceModal = true;
          }
        },
        handleComplianceClose() {
            this.showCAComplianceModal = false;
        },
        showApplicationEnhancedScreeningOptions(application) {
            this.showEnhancedScreeningResultsModal = true;
            this.application = application;
        },

        openEnhancedScreeningOptionsModal(application) {
            // this.enhancedScreeningForm.addons = application.required_addons.map(addon => addon.addon_id);
            this.application = application;
            // Clear any pending upgrade state to prevent subscription flow from triggering
            // when user adds a card for enhanced screening options
            this.pendingApplication = null;
            this.showEnhancedScreeningOptionsModal = true;
        },

        closeEnhancedScreeningOptionsModal() {
            this.enhancedScreeningForm.addons = [];
            this.showEnhancedScreeningOptionsModal = false;
        },

        copyRentersInsuranceLink() {
            navigator.clipboard.writeText(this.property.renters_insurance_link)
            this.isLinkCopied = true;
            setTimeout(() => {
                this.isLinkCopied = false;
            }, 2000);
        },

        navigateToApplication(application, delay = 0) {
            const navigate = () => {
                this.$inertia.get(this.appUrl(`/properties/${application.property_id}/application/${application.id}`));
            };
            delay > 0 ? setTimeout(navigate, delay) : navigate();
        },

        showApplicationError(applicationId, duration = 3000) {
            this.showErrorMessage[applicationId] = true;
            setTimeout(() => {
                this.showErrorMessage[applicationId] = false;
            }, duration);
        },

        handleCardAdded(eventData) {
            const { payment_method, success } = eventData || {};
            
            if (payment_method) {
                this.payment_method = payment_method.payment_method || payment_method;
            }

            // If enhanced screening modal is open, this is NOT an upgrade flow
            // Clear any pending upgrade state to prevent subscription
            if (this.showEnhancedScreeningOptionsModal) {
                this.pendingApplication = null;
            }

            // This card addition is for enhanced screening, not subscription
            // Subscription flow is handled by SubscriptionProtection component
            this.showAddCardModal = false;
            if (success || payment_method) {
                this.payment_method_success = (payment_method?.message) || 'Card added successfully';
                setTimeout(() => this.payment_method_success = null, 3000);
                
                // If there's pending enhanced screening data, process it now
                if (this.pendingEnhancedScreeningData) {
                    this.processEnhancedScreeningPayment(this.pendingEnhancedScreeningData);
                }
            }
        },

        closeCardModal() {
            this.showAddCardModal = false;
        },

        closeUpgradeLimitModal() {
            this.showUpgradeLimitModal = false;
            // Don't clear pendingApplication if we're in subscription flow
            if (!this.isInSubscriptionFlow) {
                this.pendingApplication = null;
            }
        },

        handleUpgradeClick() {
            this.showUpgradeLimitModal = false; // Close modal
            this.isInSubscriptionFlow = true; // Never open it again during subscription flow
            // RAC-10008: this CTA now upsells to RiskGuard Pro ($14.99) instead
            // of base RiskGuard ($7.99). Stash the plan in sessionStorage so the
            // SubscriptionProtection flow picks it up.
            sessionStorage.setItem('pending_subscription_plan', JSON.stringify({
                planSlug: 'protect-pro',
                billingCycle: 'monthly',
            }));
            // Pass false as second parameter to explicitly disable confirmation modal for upgrade flow
            this.$refs.subscriptionProtection.initiateSubscription(this.pendingApplication, false);
        },

        handleSubscriptionSuccess({ pendingApplication }) {
            this.payment_method_success = 'Successfully subscribed to RiskGuard!';
            setTimeout(() => this.payment_method_success = null, 5000);
            
            // Reset subscription flow flags
            this.isInSubscriptionFlow = false;
            this.isSubscribingToProtect = false;
            this.showConfirmationForSubscription = false;
            
            // Keep checkbox checked since subscription was successful
            // The checkbox will be hidden once hasProtectAccess updates via Inertia reload
            
            // Only keep enhanced screening modal open if it was already open
            // Don't open it if we're in the upgrade flow (where pendingApplication is passed but modal wasn't open)
            // The component will automatically recalculate pricing when hasProtectAccess changes
            
            // Handle navigation for upgrade flow
            if (pendingApplication) {
                this.navigateToApplication(pendingApplication);
            }
            this.pendingApplication = null;
        },

        handleSubscriptionError({ type, error }) {
            alert(error || 'An error occurred. Please try again.');
            // Reset subscription flow flags
            this.isInSubscriptionFlow = false;
            this.isSubscribingToProtect = false;
            this.showConfirmationForSubscription = false;
        },

        handleSubscriptionCancel() {
            // User cancelled the subscription flow
            // Reset subscription flow flags
            this.isInSubscriptionFlow = false;
            this.isSubscribingToProtect = false;
            this.showConfirmationForSubscription = false;
        },

        togglePropertyStatus() {
            if (this.isTogglingStatus) return;
            
            this.isTogglingStatus = true;
            
            this.$inertia.put(route('properties.toggle-status', this.property.id), {}, {
                preserveScroll: true,
                onFinish: () => {
                    this.isTogglingStatus = false;
                }
            });
        },

    }
}
</script>
